/**
 * `usePaginatedQuery` — generic wrapper around `useInfiniteQuery` for any
 * cursor-paginated oRPC procedure following the canonical pagination shape from
 * api-v2-design.md: input includes `cursor?: string`, output is
 * `{ items, nextCursor }`. The caller passes a procedure util and the input
 * fields (everything except `cursor`); the helper threads the cursor and
 * returns TanStack's `useInfiniteQuery` result unchanged — `data.pages`,
 * `hasNextPage`, `fetchNextPage`, and so on.
 *
 * `TInput` and the full page type are inferred from the procedure's
 * `ProcedureUtils`, so supplying a wrong-shaped `input` for the chosen
 * procedure is a compile error. Inferring the whole page rather than just its
 * item type keeps any extra fields a procedure returns beside `items` (e.g.
 * `spaces.list`'s `totalCount`) visible on `data.pages`. The silo and the
 * reading locale are injected the way `useSiloQuery` injects them, so a
 * paginated read's pages are scoped to the language they were fetched in.
 *
 * Only the cursor varies between pages, so the rest of the input is resolved
 * once and the per-page callback — `infiniteOptions` takes a function, since
 * the cursor changes each time — only adds the cursor to it.
 *
 * Pages are returned as-is; whether to flatten them across pages is a policy
 * the resource hook decides (e.g. `useSpaces` flattens, paginated tables may
 * keep them page-by-page).
 */
import type { ProcedureUtils } from '@orpc/react-query';
import type { LocaleSelector } from '@repo/shared/orpc/translations';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSiloLanguage } from 'component/translation';
import { useSiloKey } from 'util/datastore';

type Page = { items: unknown[]; nextCursor: string | null };
type PaginatedInput = { siloKey: string; cursor?: string; locale?: LocaleSelector };

/** A cursor-paginated oRPC procedure util (input `TInput`, page output). */
type PaginatedProcedure<TInput, TPage extends Page> = ProcedureUtils<any, TInput, TPage, any>;

export function usePaginatedQuery<TInput extends PaginatedInput, TPage extends Page>({
    procedure,
    input,
    locale,
}: {
    procedure: PaginatedProcedure<TInput, TPage>;
    input: Omit<TInput, 'cursor' | 'siloKey' | 'locale'>;
    locale?: LocaleSelector;
}) {
    const siloKey = useSiloKey();
    const currentLocale = useSiloLanguage();
    const pageInput = { ...input, siloKey, locale: locale ?? currentLocale };
    return useInfiniteQuery(
        procedure.infiniteOptions({
            input: (cursor) => ({ ...pageInput, cursor }) as TInput,
            getNextPageParam: (last) => last.nextCursor ?? undefined,
            initialPageParam: undefined as string | undefined,
        }),
    );
}
